import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import sortby from 'lodash.sortby';
import { PropTypes } from 'prop-types';

import CheckboxGroup from '@atoms/CheckboxGroup';
import Radios from '@atoms/Radios';
import LoadingArea from '@molecules/LoadingArea';
import useBusinessTypesForMemberType from '@hooks/useBusinessTypesForMemberType';

const BusinessTypeSelection = ({
    memberType,
    primaryBusinessType,
    primaryBusinessTypeFieldName,
    secondaryBusinessType,
    secondaryBusinessTypeFieldName,
    PrimaryLabel,
    SecondaryLabel,
}) => {
    const { handleChange, setFieldValue } = useFormikContext();
    const { data: businessTypes } = useBusinessTypesForMemberType(memberType);
    const [primaryTypeOptions, setPrimaryTypeOptions] = useState();
    const [secondaryTypeOptions, setSecondaryTypeOptions] = useState([]);
    const [selectedPrimaryOption, setSelectedPrimaryOption] = useState(primaryBusinessType);
    const [selectedSecondaryOption, setSelectedSecondaryOption] = useState(secondaryBusinessType);

    useEffect(() => {
        if (!businessTypes) {
            return;
        }

        setPrimaryTypeOptions(
            sortby(
                businessTypes.map(businessType => ({ value: businessType.code, label: businessType.description })),
                'label'
            )
        );
    }, [businessTypes]);

    useEffect(() => {
        if (!businessTypes) {
            return;
        }

        const primary = businessTypes.find(businessType => businessType.code === selectedPrimaryOption);
        const secondaryOptions = primary
            ? sortby(
                  primary.children.map(child => ({ value: child.code, label: child.description })),
                  'label'
              )
            : [];

        if (!secondaryOptions.find(option => option.value === selectedSecondaryOption)) {
            setSelectedSecondaryOption(null);
        }

        setSecondaryTypeOptions(secondaryOptions);
        setFieldValue('hasSecondaryTypes', secondaryOptions.length > 0);
    }, [businessTypes, selectedPrimaryOption, selectedSecondaryOption, setFieldValue]);

    if (!primaryTypeOptions) {
        return <LoadingArea />;
    }

    return (
        <div>
            {PrimaryLabel && primaryTypeOptions.length > 0 && <PrimaryLabel />}

            <Radios
                name={primaryBusinessTypeFieldName}
                value={selectedPrimaryOption}
                options={primaryTypeOptions}
                required
                onChange={e => {
                    handleChange(e);
                    setSelectedPrimaryOption(e.target.value);
                    setSelectedSecondaryOption('');
                    setFieldValue('secondaryBusinessType', '');
                }}
            />

            {SecondaryLabel && secondaryTypeOptions.length > 0 && <SecondaryLabel />}

            <CheckboxGroup
                name={secondaryBusinessTypeFieldName}
                options={secondaryTypeOptions}
                required={secondaryTypeOptions.length > 0}
            />
        </div>
    );
};

BusinessTypeSelection.propTypes = {
    memberType: PropTypes.string,
    primaryBusinessType: PropTypes.string,
    primaryBusinessTypeFieldName: PropTypes.string,
    secondaryBusinessType: PropTypes.string,
    secondaryBusinessTypeFieldName: PropTypes.string,
    PrimaryLabel: PropTypes.elementType,
    SecondaryLabel: PropTypes.elementType,
};

export default BusinessTypeSelection;
